import { NgClass } from '@angular/common';
import { AfterViewInit, Component, ElementRef, effect, inject, input, model, viewChild, output } from '@angular/core';
import { AgChart, AgChartOptions } from 'ag-charts-community';
import {
  AgChartClickEvent,
  AgChartInstance,
  AgChartLegendClickEvent,
  AgPieSeriesTooltipRendererParams,
  AgSeriesNodeClickParams,
} from 'ag-charts-community/dist/esm/es6/chart/agChartOptions';
import { isObservable, Observable, of } from 'rxjs';

import { BaseComponent } from '../../base/base.component';
import { Colours, PieChartSize } from '../../../constants';
import { ThemeService } from '../../../services';

@Component({
  selector: 'app-pie-chart',
  templateUrl: './pie-chart.component.html',
  styleUrl: './pie-chart.component.scss',
  imports: [NgClass],
})
export class PieChartComponent<T> extends BaseComponent implements AfterViewInit {
  private readonly chart = viewChild<ElementRef<HTMLDivElement>>('chart');

  public readonly header = input('');
  public readonly subHeader = input('');
  public readonly labelKey = input<string>();
  public readonly valueKey = input<string>();
  public readonly valueFormattedKey = input<string>();
  public readonly tooltipKey = input<string>();
  public readonly showLegend = input(true);
  public readonly data = model<Observable<T[]> | T[]>();
  public readonly pieChartSize = input<PieChartSize>();
  public readonly clicked = output<string>();
  public readonly togglePreview = output();

  public get extraLarge() {
    return this.pieChartSize() === PieChartSize.extraLarge;
  }
  public get large() {
    return this.pieChartSize() === PieChartSize.large;
  }
  public get medium() {
    return this.pieChartSize() === PieChartSize.medium;
  }
  public get small() {
    return this.pieChartSize() === PieChartSize.small;
  }

  private readonly themeService = inject(ThemeService);

  private agChart: AgChartInstance;
  private options: AgChartOptions;
  private latestData: T[];
  private canPreview: boolean;

  // A chart already on screen has to follow a change of appearance, not wait for its next data
  // load — the user switches to Dark and the chart is still glowing white behind a dark page.
  private readonly themeEffect = effect(() => {
    // ⚠️ Read the signal HERE, not only inside getOptions(). This effect first runs at
    // construction, before ngAfterViewInit draws the chart, so `agChart` is undefined and the guard
    // below skips getOptions entirely. An effect that reads no signal on a run ends that run with
    // no dependencies and is never scheduled again - the chart would silently stop following the
    // appearance. (Raised in review, 2026-09-17.)
    this.themeService.isDark();
    if (this.agChart) {
      this.getOptions(this.latestData ?? []);
      AgChart.update(this.agChart, this.options);
    }
  });

  private colours = [
    Colours.blue,
    Colours.green,
    Colours.orange,
    Colours.red,
    Colours.indigo,
    Colours.cyan,
    Colours.yellow,
    Colours.purple,
    Colours.teal,
    Colours.pink,
  ];

  ngAfterViewInit() {
    this.drawChart();
  }

  preview() {
    this.canPreview = true;
    setTimeout(() => {
      if (this.canPreview) {
        this.togglePreview.emit();
      }
    }, 100);
  }

  private drawChart() {
    ((isObservable(this.data()) ? this.data() : of(this.data())) as Observable<T[]>)
      .pipe(this.untilDestroyed())
      .subscribe((data: T[]) => {
        this.latestData = data;
        this.getOptions(data);
        if (!this.agChart) {
          this.agChart = AgChart.create(this.options);
        } else {
          AgChart.update(this.agChart, this.options);
        }
      });
  }

  private getOptions(data: T[]) {
    this.options = {
      autoSize: true,
      container: this.chart().nativeElement,
      // ag-charts ships both themes; the dark one gives readable labels and a dark canvas.
      theme: this.themeService.isDark() ? 'ag-default-dark' : 'ag-default',
      title: {
        text: this.header(),
      },
      subtitle: {
        text: this.subHeader(),
      },
      series: [
        {
          data: data,
          type: 'pie',
          angleKey: this.valueKey(),
          calloutLabelKey: this.valueFormattedKey(),
          legendItemKey: this.tooltipKey(),
          calloutLabel: {
            minAngle: 1,
          },
          tooltip: {
            renderer: (params: AgPieSeriesTooltipRendererParams) => ({
              content: params.datum[this.tooltipKey()],
            }),
          },
          fills: this.colours,
          strokes: this.colours,
          highlightStyle: {
            item: {
              fill: Colours.gray300,
            },
          },
          listeners: {
            nodeClick: (event: AgSeriesNodeClickParams<any>) => this.clicked.emit(event.datum[this.labelKey()]),
          },
        },
      ],
      legend: {
        enabled: this.showLegend(),
        position: 'right',
        item: {
          toggleSeriesVisible: false,
          maxWidth: this.large ? undefined : 120,
        },
        listeners: {
          legendItemClick: (event: AgChartLegendClickEvent) => {
            this.canPreview = false;
            this.clicked.emit(this.latestData[event.itemId][this.labelKey()]);
          },
        },
      },
      listeners: {
        click: (event: AgChartClickEvent) => this.preview(),
      },
    } as AgChartOptions;
  }
}
